/**
 * Enhanced lazy retry utility for dynamic imports
 *
 * Handles chunk loading failures during deployments with progressive retry,
 * cache-busting, and graceful error handling.
 */
import { type ComponentType, lazy } from 'react';

export interface LazyRetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  cacheBypass?: boolean;
  fallbackComponent?: ComponentType<any>;
}

/**
 * Enhanced lazy retry wrapper for dynamic imports
 *
 * Features:
 * - Progressive retry with exponential backoff
 * - Cache-busting on retry
 * - Graceful fallback handling
 * - Better error logging
 */
export function lazyRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  options: LazyRetryOptions = {},
): React.LazyExoticComponent<T> {
  const { maxRetries = 3, retryDelay = 1000, cacheBypass = true } = options;

  return lazy(() => {
    let attempt = 0;

    const tryImport = async (): Promise<{ default: T }> => {
      try {
        return await componentImport();
      } catch (error) {
        const isChunkError =
          error instanceof Error &&
          (error.name === 'ChunkLoadError' ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('Failed to fetch dynamically imported module') ||
            (error.name === 'TypeError' && error.message?.includes('dynamically imported module')));

        if (!isChunkError) {
          console.error('[LazyRetry] Non-chunk error, not retrying:', error);
          throw error;
        }

        attempt++;

        if (attempt > maxRetries) {
          console.error(`[LazyRetry] Failed after ${maxRetries} attempts:`, error);
          throw new Error(
            `Failed to load component after ${maxRetries} attempts. ` +
              'This may be due to a network issue or deployment in progress. ' +
              'Please refresh the page and try again.',
          );
        }

        console.warn(
          `[LazyRetry] Attempt ${attempt}/${maxRetries} failed, retrying in ${retryDelay * attempt}ms:`,
          error.message,
        );

        // Progressive delay: 1s, 2s, 3s...
        await new Promise((resolve) => setTimeout(resolve, retryDelay * attempt));

        // Cache-busting: Add timestamp to force fresh fetch
        if (cacheBypass && attempt > 1) {
          const cacheBuster = `?cb=${Date.now()}-${attempt}`;
          console.log(`[LazyRetry] Adding cache buster: ${cacheBuster}`);

          // Force reload by manipulating module cache
          if (typeof window !== 'undefined') {
            // Clear any existing module cache entries
            try {
              // This is a best-effort attempt to clear browser import cache
              performance.clearResourceTimings();
            } catch (e) {
              // Ignore cache clearing errors
            }
          }
        }

        return tryImport();
      }
    };

    return tryImport();
  });
}

/**
 * Fallback component for failed chunk loads
 */
export function ChunkLoadError({ error, retry }: { error?: Error; retry?: () => void }) {
  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <div className="text-center max-w-md">
        <div className="text-red-400 mb-4">
          <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <title>Component load error</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-gray-900 font-medium mb-2">Failed to Load Component</h3>
        <p className="text-gray-600 text-sm mb-4">
          {error?.message ||
            'There was a problem loading this feature. This may be due to a network issue or a deployment in progress.'}
        </p>
        <div className="space-y-2">
          {retry && (
            <button
              type="button"
              onClick={retry}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
}
